import React from 'react';
import { useTranslation } from 'react-i18next';

import { LineChart, Skeleton, type ChartSeries } from '../../../../components';
import { HOME, WEBSITE_ANALYTICS } from '../../../../theme/tokens';
import { Legend, StatCard } from '../../../stats/components';
import type { TrendPoint } from '../../queries';
import { trendLabels } from '../../format';
import { Body, ChartBox, Quiet, LegendWrap } from './TrafficTrend.styles';

const CHART_HEIGHT = 200;

/** Compact count axis ("80", "1.2k"). */
const countAxis = (n: number): string => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : `${Math.round(n)}`);

export type TrafficTrendProps = {
  points: TrendPoint[];
  visitorsAvailable?: boolean;
  hourly: boolean;
  loading?: boolean;
  error?: boolean;
  /** Quality mode: the counts are people, and so are the words. */
  quality?: boolean;
  /** The suspected subset of each point, drawn as its own line (people, or pages viewed when People counts are
   *  unavailable). */
  suspected?: TrendPoint[];
  /** Quality mode: traffic the rules excluded, kept as a diagnostic line outside the main People line. */
  excluded?: TrendPoint[];
};

/** "Visitors over time": visitors and pages viewed per hour or day across the period. */
export const TrafficTrend = ({
  points,
  visitorsAvailable = true,
  hourly,
  loading = false,
  error = false,
  quality = false,
  suspected,
  excluded,
}: TrafficTrendProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const labels = React.useMemo(
    () =>
      trendLabels(
        points.map((p) => p.at),
        hourly,
        i18n.language,
      ),
    [points, hourly, i18n.language],
  );
  const visitorsName = quality ? t('People') : t('Visitors');
  const series: ChartSeries[] = [
    ...(visitorsAvailable
      ? [{ name: visitorsName, color: WEBSITE_ANALYTICS.visitors, points: points.map((p) => p.visitors) }]
      : []),
    { name: t('Pages viewed'), color: WEBSITE_ANALYTICS.pages, points: points.map((p) => p.views) },
    ...(suspected
      ? [
          {
            name: t('Suspected'),
            color: HOME.t400,
            dashed: true,
            points: suspected.map((p) => (visitorsAvailable ? p.visitors : p.views)),
          },
        ]
      : []),
    ...(excluded
      ? [
          {
            name: t('Excluded'),
            color: HOME.danger,
            dashed: true,
            points: excluded.map((p) => (visitorsAvailable ? p.visitors : p.views)),
          },
        ]
      : []),
  ];
  const empty =
    !loading &&
    !error &&
    [points, suspected ?? [], excluded ?? []].every((line) => line.every((p) => p.visitors === 0 && p.views === 0));
  return (
    <StatCard
      title={
        visitorsAvailable ? (quality ? t('People over time') : t('Visitors over time')) : t('Pages viewed over time')
      }
      sub={
        !visitorsAvailable
          ? t('Pages viewed for this selection')
          : hourly
            ? quality
              ? t('How many people came, hour by hour')
              : t('How many people came, hour by hour')
            : quality
              ? t('How many people came, day by day')
              : t('How many people came, day by day')
      }
    >
      <Body>
        <ChartBox $height={CHART_HEIGHT}>
          {loading ? (
            <Skeleton block height={CHART_HEIGHT} radius={12} />
          ) : error ? (
            <Quiet text={t('We could not load this chart. Try again in a moment.')} tag="span" raw />
          ) : (
            <>
              <LineChart
                series={series}
                xLabels={labels.xLabels}
                pointLabels={labels.pointLabels}
                height={CHART_HEIGHT}
                yTicks={3}
                showY
                formatY={countAxis}
              />
              {empty ? <Quiet text={t('No visits in this period yet.')} tag="span" raw /> : null}
            </>
          )}
        </ChartBox>
        <LegendWrap>
          <Legend
            items={[
              ...(visitorsAvailable ? [{ name: visitorsName, color: WEBSITE_ANALYTICS.visitors }] : []),
              { name: t('Pages viewed'), color: WEBSITE_ANALYTICS.pages },
              ...(suspected ? [{ name: t('Suspected'), color: HOME.t400, dashed: true }] : []),
              ...(excluded ? [{ name: t('Excluded'), color: HOME.danger, dashed: true }] : []),
            ]}
          />
        </LegendWrap>
      </Body>
    </StatCard>
  );
};

export default TrafficTrend;
